<template>
  <!--
    Rechte Seite „Beteiligte zuordnen“ (Kundenfeedback 18.10.2026, XII/XIII;
    24.10.2026, XVIII/XX): drei Reiter als weiße Pillen „Suche gesamt“ |
    „Favoriten“ | „Eigene Kontakte“ (ausgewählt blau, Erklärung als Tooltip),
    darunter zuerst die in der Akte erwähnten Adressen, dann nur das Suchfeld
    und die Treffer. Optional filtern Pillen nach Rolle; unten „Neue Adresse
    anlegen“.
  -->
  <section class="csp" data-testid="apd-search-pane">
    <div class="kars-pills" role="tablist" :aria-label="$gt('Suchbereich')">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        role="tab"
        class="kars-pill"
        :class="{ 'is-active': tab === t.key }"
        :aria-selected="tab === t.key"
        :data-testid="'apd-tab-' + t.key"
        @click="selectTab(t.key)"
      >
        {{ $gt(t.label) }}
        <span v-if="t.key === 'favorites'" class="kars-pill__count">{{ favoriteCount }}</span>
        <span v-else-if="t.key === 'own'" class="kars-pill__count">{{ contacts.length }}</span>
        <v-tooltip activator="parent" location="bottom" :open-delay="250" max-width="320">{{ $gt(t.hint) }}</v-tooltip>
      </button>
    </div>

    <p v-if="targetRole" class="csp__context" data-testid="apd-target-role">
      {{ $gt('Zuordnen als') }} <strong>{{ roleLabelOf(targetRole) }}</strong>
      <button type="button" class="csp__link" @click="$emit('clear-role')">{{ $gt('Rolle aufheben') }}</button>
    </p>

    <!-- XX: In der Akte erwähnte Adressen zuerst – ohne eigene Hintergrundfarbe -->
    <div v-if="tab === 'all' && visibleSuggestions.length" class="csp__mentioned">
      <p class="csp__group">{{ $gt('Aus der Akte bekannt') }}</p>
      <ul class="csp__list">
        <li v-for="s in visibleSuggestions" :key="s.key" class="csp__sugg" :data-testid="'apd-suggestion-' + s.role">
          <div class="csp__sugg-text">
            <span class="csp__sugg-name">{{ s.name }}</span>
            <span class="csp__sugg-origin">{{ $gt(s.origin) }} · {{ roleLabelOf(s.role) }}</span>
          </div>
          <span class="csp__badge-case" tabindex="0" data-testid="apd-suggestion-badge">
            {{ $gt('In Akte erwähnt') }}
            <v-tooltip activator="parent" location="top" :open-delay="250" max-width="320">
              {{ $gt('Im Fragebogen bzw. in den Falldaten genannt, aber noch nicht als Beteiligter zugeordnet.') }}
            </v-tooltip>
          </span>
          <button type="button" class="csp__ghost" data-testid="apd-suggestion-search" @click="startSearch(s.name)">
            {{ $gt('Suchen') }}
            <v-tooltip activator="parent" location="top" :open-delay="250" max-width="320">
              {{ $gt('Sucht diesen Namen in Ihren Kontakten und bei allen in KARS registrierten Unternehmen. Den passenden Treffer ordnen Sie dann mit „Zuordnen“ zu.') }}
            </v-tooltip>
          </button>
          <button type="button" class="csp__ghost" data-testid="apd-suggestion-create" @click="$emit('create-new', { name: s.name, role: s.role })">
            {{ $gt('Neu anlegen') }}
            <v-tooltip activator="parent" location="top" :open-delay="250" max-width="320">
              {{ $gt('Legt eine neue Adresse mit diesem Namen an und ordnet sie gleich in dieser Rolle der Akte zu – für Kontakte, die es noch nicht gibt.') }}
            </v-tooltip>
          </button>
        </li>
      </ul>
    </div>

    <div class="csp__search">
      <Search :size="15" class="csp__search-ico" aria-hidden="true" />
      <input
        ref="input"
        :value="query"
        class="csp__input"
        :placeholder="$gt(placeholder)"
        :aria-label="$gt(placeholder)"
        data-testid="apd-search-input"
        @input="onInput($event.target.value)"
      />
      <button v-if="query" type="button" class="csp__clear" :aria-label="$gt('Suche leeren')" @click="onInput('')">
        <X :size="13" aria-hidden="true" />
      </button>
    </div>

    <div v-if="chips.length" class="kars-pills" role="group" :aria-label="$gt('Nach Rolle filtern')">
      <button
        v-for="c in chips"
        :key="c.value || 'all'"
        type="button"
        class="kars-pill kars-pill--sm"
        :class="{ 'is-active': roleFilter === c.value }"
        :aria-pressed="roleFilter === c.value"
        @click="roleFilter = c.value"
      >{{ $gt(c.label) }}</button>
    </div>

    <div class="csp__results">
      <!-- Suche gesamt – XVIII.2: kein zusätzlicher Hinweiskasten, der Platzhalter genügt -->
      <template v-if="tab === 'all'">
        <p v-if="search.isQueryLongEnough.value && search.loading.value" class="csp__empty"><v-progress-circular indeterminate size="14" width="2" color="primary" /> {{ $gt('Suche läuft …') }}</p>
        <p v-else-if="search.isQueryLongEnough.value && search.error.value" class="csp__empty csp__empty--error">{{ $gt('Suche fehlgeschlagen:') }} {{ search.error.value }}</p>
        <template v-else-if="search.isQueryLongEnough.value && search.searched.value">
          <template v-for="g in allGroups" :key="g.source">
            <p class="csp__group">{{ $gt(g.label) }} <span class="csp__group-count">{{ g.hits.length }}</span></p>
            <ul class="csp__list">
              <ContactResultRow v-for="h in g.hits" :key="h.key" v-bind="rowProps(h)" v-on="rowListeners" />
            </ul>
          </template>
          <div v-if="!filteredAll.length" class="csp__none" data-testid="apd-no-hits">
            <p>{{ $gt('Kein Treffer für') }} „{{ query.trim() }}“.</p>
            <button type="button" class="csp__primary" data-testid="apd-new-from-empty" @click="emitCreate">{{ $gt('Neue Adresse anlegen') }}</button>
          </div>
        </template>
      </template>

      <!-- Favoriten, gruppiert nach Standardrolle -->
      <template v-else-if="tab === 'favorites'">
        <template v-for="g in favGroups" :key="g.role">
          <p class="csp__group">{{ g.label }}</p>
          <ul class="csp__list">
            <ContactResultRow v-for="h in g.hits" :key="h.key" v-bind="rowProps(h)" favorite-mode v-on="rowListeners" />
          </ul>
        </template>
        <p v-if="!favGroups.length" class="csp__empty">
          {{ query ? $gt('Kein Favorit passt zur Suche.') : $gt('Noch keine Favoriten. Markieren Sie Ihre Standard-Partner (z. B. Sachverständiger, Werkstatt, Anwalt) mit dem Stern – die Rolle des Kontakts wird zur Standardrolle.') }}
        </p>
      </template>

      <!-- Eigene Kontakte -->
      <template v-else>
        <ul v-if="filteredOwn.length" class="csp__list">
          <ContactResultRow v-for="h in filteredOwn" :key="h.key" v-bind="rowProps(h)" v-on="rowListeners" />
        </ul>
        <p v-else class="csp__empty">{{ contacts.length ? $gt('Kein eigener Kontakt passt zur Suche.') : $gt('Ihr Adressbuch ist noch leer.') }}</p>
      </template>
    </div>

    <footer class="csp__create">
      <span>
        {{ $gt('Nicht dabei? Jede Adresse kann in jeder Rolle zugeordnet werden.') }}
        <button v-if="tab !== 'all'" type="button" class="csp__link" @click="$emit('open-contacts')">
          {{ tab === 'favorites' ? $gt('Favoriten verwalten') : $gt('Adressbuch öffnen') }}
        </button>
      </span>
      <button type="button" class="csp__outline" data-testid="apd-new-address-open" @click="emitCreate">{{ $gt('Neue Adresse anlegen') }}</button>
    </footer>
  </section>
</template>

<script>
import { Search, X } from "lucide-vue-next";
import { useContactSearch } from "@/composables/useContactSearch";
import { groupHitsBySource } from "@/utils/contactSearchHits";
import ContactResultRow from "./ContactResultRow.vue";
import {
  SEARCH_TABS,
  favoriteGroups,
  filterCaseSuggestions,
  filterHitsByRole,
  hitAlreadyInCase,
  ownContactHits,
  roleChoices,
  roleFilterChips,
  roleLabel,
} from "./participantDialogModel";

const PLACEHOLDERS = {
  all: "Name, Firma, Ort oder E-Mail suchen …",
  favorites: "Favoriten durchsuchen …",
  own: "Eigene Kontakte durchsuchen …",
};

export default {
  name: "ContactSearchPanel",

  components: { ContactResultRow, Search, X },

  props: {
    contacts: { type: Array, default: () => [] },
    participants: { type: Array, default: () => [] },
    /** CaseSuggestion[] aus buildCaseSuggestions() */
    suggestions: { type: Array, default: () => [] },
    targetRole: { type: String, default: "" },
    caseMuted: { type: Boolean, default: false },
    /** Schlüssel des Treffers, der gerade zugeordnet wird */
    busyKey: { type: String, default: "" },
  },

  emits: ["assign", "toggle-favorite", "drag-start", "drag-end", "create-new", "open-contacts", "query-change", "clear-role"],

  setup(props) {
    const search = useContactSearch({ knownContacts: () => props.contacts });
    return { search };
  },

  data() {
    return { tab: "all", query: "", roleFilter: "", tabs: SEARCH_TABS, choices: roleChoices() };
  },

  computed: {
    placeholder() { return PLACEHOLDERS[this.tab]; },
    favoriteCount() { return this.contacts.filter((c) => c.isFavorite).length; },
    visibleSuggestions() { return filterCaseSuggestions(this.suggestions, this.query); },
    ownHits() { return ownContactHits(this.contacts, this.query); },
    favGroups() { return favoriteGroups(this.contacts, this.query); },
    baseList() { return this.tab === "all" ? this.search.hits.value : this.tab === "own" ? this.ownHits : []; },
    chips() { return roleFilterChips(this.baseList); },
    filteredAll() { return filterHitsByRole(this.search.hits.value, this.roleFilter); },
    allGroups() { return groupHitsBySource(this.filteredAll); },
    filteredOwn() { return filterHitsByRole(this.ownHits, this.roleFilter); },
    rowListeners() {
      return {
        assign: (payload) => this.$emit("assign", payload),
        "toggle-favorite": (hit) => this.$emit("toggle-favorite", hit),
        "drag-start": (hit) => this.$emit("drag-start", hit),
        "drag-end": () => this.$emit("drag-end"),
      };
    },
  },

  watch: {
    chips(list) {
      if (this.roleFilter && !list.some((c) => c.value === this.roleFilter)) this.roleFilter = "";
    },
  },

  methods: {
    roleLabelOf(role) { return roleLabel(role); },
    rowProps(hit) {
      return {
        hit,
        roleChoices: this.choices,
        presetRole: this.targetRole,
        inCase: hitAlreadyInCase(hit, this.participants),
        caseMuted: this.caseMuted,
        busy: this.busyKey === hit.key,
      };
    },
    selectTab(key) {
      this.tab = key;
      this.roleFilter = "";
      this.$nextTick(() => this.$refs.input?.focus?.());
    },
    onInput(value) {
      this.query = value;
      this.search.setQuery(value);
      this.$emit("query-change", value);
    },
    /**
     * Von außen: Suchbegriff setzen und in „Suche gesamt“ wechseln.
     * focus=false, wenn der Begriff links getippt wird (Fokus bleibt dort).
     */
    startSearch(value, { focus = true } = {}) {
      this.tab = "all";
      this.roleFilter = "";
      this.onInput(value || "");
      if (focus) this.$nextTick(() => this.$refs.input?.focus?.());
    },
    focusInput() { this.$nextTick(() => this.$refs.input?.focus?.()); },
    /** Nach Änderungen (Übernahme, Favorit) die Gesamtsuche auffrischen. */
    refresh() { return this.search.refresh(); },
    emitCreate() {
      this.$emit("create-new", { name: this.query.trim(), role: this.targetRole });
    },
  },
};
</script>

<style scoped>
.csp { display: flex; flex-direction: column; gap: 12px; min-height: 0; flex: 1; }
.csp > .kars-pills { align-self: flex-start; }

.csp__context { margin: -2px 0 0; font-size: 12.5px; color: var(--dark-color); }
.csp__link { margin-left: 6px; border: none; background: none; padding: 0; font: inherit; font-size: 12.5px; font-weight: 600; color: var(--primary-color); cursor: pointer; text-decoration: underline; }

.csp__search {
  display: flex; align-items: center; gap: 8px; height: 40px; padding: 0 12px;
  border: 1px solid var(--border-grey-color); border-radius: 11px; background: #fff;
  transition: border-color var(--duration-fast, 150ms) ease, box-shadow var(--duration-fast, 150ms) ease;
}
.csp__search:focus-within { border-color: var(--primary-color); box-shadow: 0 0 0 3px var(--primary-light-color-transparent); }
.csp__search-ico { flex-shrink: 0; color: var(--grey-color); }
.csp__input { flex: 1; min-width: 0; border: none; outline: none; background: transparent; font: inherit; font-size: 13.5px; color: var(--dark-color); }
.csp__input::placeholder { color: var(--disabled-grey-color); }
.csp__clear { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border: none; border-radius: 50%; background: var(--grey-medium-color); color: var(--grey-color); cursor: pointer; }

.csp__results { flex: 1; min-height: 160px; overflow-y: auto; padding-right: 2px; }
.csp__group { margin: 12px 0 6px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--grey-color); }
.csp__group:first-child { margin-top: 2px; }
.csp__group-count { margin-left: 4px; font-weight: 600; color: var(--disabled-grey-color); }
.csp__list { list-style: none; margin: 0; padding: 0; }
.csp__empty { display: flex; align-items: center; gap: 8px; margin: 6px 0; font-size: 12.5px; line-height: 1.45; color: var(--grey-color); }
.csp__empty--error { color: #b42318; }

/* XX: „nicht wieder in einer eigenen Farbe hinterlegt“ – weiße Zeilen wie die Treffer. */
.csp__sugg {
  display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 11px;
  background: #fff; border: 1px solid var(--border-grey-color-light);
}
.csp__sugg + .csp__sugg { margin-top: 6px; }
.csp__sugg-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.csp__sugg-name { font-size: 13.5px; font-weight: 650; color: var(--dark-color); }
.csp__sugg-origin { font-size: 12px; color: var(--grey-color); }
.csp__badge-case {
  padding: 2px 8px; border-radius: 999px; border: 1px solid var(--border-grey-color); background: #fff;
  color: var(--grey-color); font-size: 11px; font-weight: 600; white-space: nowrap; cursor: help;
}
.csp__ghost {
  height: 28px; padding: 0 10px; border: 1px solid var(--border-grey-color); border-radius: 8px; background: #fff;
  font: inherit; font-size: 12px; font-weight: 600; color: var(--dark-color); cursor: pointer; white-space: nowrap;
}
.csp__ghost:hover { border-color: var(--primary-color); color: var(--primary-color); }

.csp__none { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 14px 16px; border-radius: 11px; background: #fff; border: 1px solid var(--border-grey-color-light); }
.csp__none p { margin: 0; font-size: 13px; color: var(--dark-color); }
.csp__primary, .csp__outline { height: 34px; padding: 0 14px; border-radius: 9px; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.csp__primary { border: none; background: var(--primary-color); color: #fff; }
.csp__outline { border: 1px solid var(--primary-color); background: #fff; color: var(--primary-color); }
.csp__outline:hover { background: var(--primary-light-color-transparent); }
.csp__primary:focus-visible, .csp__outline:focus-visible, .csp__ghost:focus-visible, .csp__link:focus-visible, .csp__badge-case:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }

.csp__create {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding-top: 10px; border-top: 1px solid var(--border-grey-color-light);
  font-size: 12.5px; color: var(--grey-color);
}
@media (prefers-reduced-motion: reduce) { .csp__search { transition: none; } }
</style>
